import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Zap, ShieldCheck, MessageSquare, Download, Layers,
  Megaphone, BarChart3, DollarSign, Boxes, Award, MapPin, ArrowRight,
} from 'lucide-react';
import '../../i18n';
import './LandingV2.css';
import HeroV2 from './HeroV2';
import DemoInteractiveV2 from './DemoInteractiveV2';
import PricingV2 from './PricingV2';
import WaitlistModal from '../WaitlistModal';
import V2Layout, { WP_URL } from './V2Layout';

const PLATFORMS = [
  { name: 'WooCommerce', logo: '/images/logos/woocommerce_logo.webp', waitlist: false },
  { name: 'Shopify', logo: '/images/logos/shopify_logo.webp', waitlist: true },
  { name: 'BigCommerce', logo: '/images/logos/bigcommerce_logo.webp', waitlist: true },
  { name: 'Magento', logo: '/images/logos/magento_logo.webp', waitlist: true },
  { name: 'PrestaShop', logo: '/images/logos/prestashop_logo.webp', waitlist: true },
];

const DEPT_ICONS = {
  marketing: <Megaphone />,
  sales: <BarChart3 />,
  finance: <DollarSign />,
  stock: <Boxes />,
};

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.v2-reveal');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// When arriving on the home page with a section in the hash (e.g. "/#features"
// from another page's nav), scroll to that section once it is rendered.
function useHashScroll() {
  useEffect(() => {
    const id = window.location.hash.replace(/^#/, '');
    if (!id || id === 'privacy' || id === 'mentions') return;
    const el = document.getElementById(id);
    if (el) {
      const timer = setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 120);
      return () => clearTimeout(timer);
    }
  }, []);
}

export default function LandingV2() {
  const { t } = useTranslation();
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  useReveal();
  useHashScroll();

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const domains = ['marketing', 'sales', 'finance', 'stock'].map((type) => ({
    type,
    icon: DEPT_ICONS[type],
    label: t(`domains.${type}.label`),
    title: t(`domains.${type}.title`),
    description: t(`domains.${type}.description`),
    question: t(`domains.${type}.question`),
  }));

  const features = [
    { icon: <MessageSquare />, title: t('features.natural.title'), description: t('features.natural.description') },
    { icon: <Zap />, title: t('features.instant.title'), description: t('features.instant.description') },
    { icon: <ShieldCheck />, title: t('features.secure.title'), description: t('features.secure.description') },
    { icon: <Download />, title: t('features.export.title'), description: t('features.export.description') },
  ];

  return (
    <V2Layout isHome>
      {/* ---------------- HERO (top demo) ---------------- */}
      <HeroV2 />

      {/* ---------------- DEPARTMENTS ---------------- */}
      <section id="domaines" className="v2-section">
        <div className="v2-container">
          <div className="v2-head v2-reveal">
            <span className="v2-pill">
              <Layers />
              {t('domains.badge')}
            </span>
            <h2>{t('domains.title')}</h2>
            <p>{t('domains.subtitle')}</p>
          </div>
          <div className="v2-dept-grid">
            {domains.map((d, i) => (
              <div className="v2-dept v2-reveal" key={d.type} style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="v2-dept-ic">{d.icon}</div>
                <div className="v2-dept-label">{d.label}</div>
                <h3>{d.title}</h3>
                <p>{d.description}</p>
                <div className="v2-dept-q">
                  <MessageSquare />
                  <span>"{d.question}"</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FEATURES ---------------- */}
      <section id="features" className="v2-section soft">
        <div className="v2-container">
          <div className="v2-head v2-reveal">
            <h2>{t('features.title')}</h2>
            <p>{t('features.subtitle')}</p>
          </div>
          <div className="v2-feat-grid">
            {features.map((f, i) => (
              <div className="v2-feat v2-reveal" key={f.title} style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="v2-feat-head">
                  <div className="v2-feat-ic">{f.icon}</div>
                  <h3>{f.title}</h3>
                </div>
                <p>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- INTEGRATIONS ---------------- */}
      <section id="integrations" className="v2-section">
        <div className="v2-container">
          <div className="v2-head v2-reveal">
            <h2>{t('integrations.title')}</h2>
            <p>{t('integrations.subtitle')}</p>
          </div>
          {/* Featured platform — available now, full width */}
          {PLATFORMS.filter((p) => !p.waitlist).map((p) => (
            <div className="v2-int-featured v2-reveal" key={p.name}>
              <div className="v2-int-logo">
                <img src={p.logo} alt={p.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
              <button className="v2-int-btn v2-int-btn-green" onClick={() => window.open(WP_URL, '_blank')}>{t('integrations.visit_marketplace')}</button>
            </div>
          ))}

          {/* Coming soon */}
          <div className="v2-int-soon-head v2-reveal">
            <span className="v2-int-soon-line" />
            <span className="v2-int-soon-label">{t('integrations.coming_soon')}</span>
            <span className="v2-int-soon-line" />
          </div>
          <div className="v2-int-grid">
            {PLATFORMS.filter((p) => p.waitlist).map((p, i) => (
              <div className="v2-int-card v2-reveal" key={p.name} style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="v2-int-logo">
                  <img src={p.logo} alt={p.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
                <span className="v2-int-status">{t('integrations.waiting_list')}</span>
                <button className="v2-int-btn" onClick={() => setSelectedPlatform(p.name)}>{t('subscribe.button')}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- BENEFITS ---------------- */}
      <section className="v2-section soft">
        <div className="v2-container">
          <div className="v2-head v2-reveal">
            <h2>{t('benefits.title')}</h2>
          </div>
          <div className="v2-benefit-grid">
            <div className="v2-benefit v2-reveal">
              <div className="v2-benefit-num v2-grad-text">{t('benefits.time.stat', '10 Hours +')}</div>
              <h3>{t('benefits.time.title')}</h3>
              <p>{t('benefits.time.description')}</p>
            </div>
            <div className="v2-benefit v2-reveal" style={{ transitionDelay: '80ms' }}>
              <div className="v2-benefit-num v2-grad-text">{t('benefits.decisions.stat', 'Faster +')}</div>
              <h3>{t('benefits.decisions.title')}</h3>
              <p>{t('benefits.decisions.description')}</p>
            </div>
            <div className="v2-benefit v2-reveal" style={{ transitionDelay: '160ms' }}>
              <div className="v2-benefit-num v2-grad-text">{t('benefits.team.stat', 'Access +')}</div>
              <h3>{t('benefits.team.title')}</h3>
              <p>{t('benefits.team.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- DEMO (interactive, bottom) ---------------- */}
      <DemoInteractiveV2 />

      {/* ---------------- SOCIAL PROOF ---------------- */}
      <section className="v2-social">
        <div className="v2-container">
          <div className="v2-social-stats v2-reveal">
            <div className="v2-social-stat">
              <div className="v2-social-num v2-grad-text">50+</div>
              <div className="v2-social-label">{t('social_proof.active_stores')}</div>
            </div>
            <div className="v2-social-stat">
              <div className="v2-social-num v2-grad-text">10h</div>
              <div className="v2-social-label">{t('social_proof.saved_per_week')}</div>
            </div>
            <div className="v2-social-stat">
              <div className="v2-social-num v2-grad-text">&lt;5s</div>
              <div className="v2-social-label">{t('social_proof.avg_response_time')}</div>
            </div>
            <div className="v2-social-stat">
              <div className="v2-social-num v2-grad-text">8</div>
              <div className="v2-social-label">{t('social_proof.supported_languages')}</div>
            </div>
          </div>
          <div className="v2-social-badges v2-reveal">
            <span className="v2-social-badge"><Award /> {t('social_proof.woocommerce_certified')}</span>
            <span className="v2-social-badge"><ShieldCheck /> {t('social_proof.gdpr_compliant')}</span>
            <span className="v2-social-badge"><MapPin /> {t('social_proof.hosted_france')}</span>
          </div>
        </div>
      </section>

      {/* ---------------- PRICING ---------------- */}
      <PricingV2 />

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="v2-cta-wrap">
        <div className="v2-container">
          <div className="v2-cta v2-reveal">
            <h2>{t('cta.cta_title')}</h2>
            <p>{t('cta.cta_subtitle')}</p>
            <div className="v2-cta-actions">
              <a className="v2-btn v2-btn-primary v2-btn-lg" href={WP_URL} target="_blank" rel="noreferrer">
                {t('cta.install_free')} <ArrowRight />
              </a>
              <a className="v2-btn v2-btn-ghost v2-btn-lg" href="#demo" onClick={(e) => scrollTo(e, 'demo')}>
                {t('cta.view_demo')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <WaitlistModal
        isOpen={!!selectedPlatform}
        onClose={() => setSelectedPlatform(null)}
        platform={selectedPlatform}
      />
    </V2Layout>
  );
}
